import { DownOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/core';
import { Select } from 'antd';
import React, { useState, useEffect } from 'react';
import CButton from 'src/component/Button';
import Copy from 'src/component/Copy';
import CustomDatePicker from 'src/component/DatePicker';
import InnerHeader from 'src/component/InnerHeader';
import { CStandardInput } from 'src/component/Input';
import Modal from 'src/component/Modal/Modal';
import SelectUsersElement from 'src/component/SelectUsersElement';
import { UsersSelectData } from 'src/component/SelectUsersElement/interfaces/usersSelect.interface';
import NoxTable from 'src/component/Table';
import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import { formatDate } from 'src/helpers/millisecondsToFormattedDate';
import { CustomTimeZones } from 'src/reactQuery/events/enums/events.enum';
import { CreateEventData, EventData } from 'src/reactQuery/events/interfaces/events.interface';
import { useCreateEvent } from 'src/reactQuery/events/useCreateEvent';
import { useGetEvents } from 'src/reactQuery/events/useGetEvents';

interface EventsInterface {}
const { Option } = Select;

const useStyles = makeStyles({
  dateLabel: {
    color: '#9BA0AD',
    fontSize: '12px'
  },
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    marginTop: '16px'
  }
});

const Events = () => {
  const [events, setEvents] = useState<EventsInterface>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedUsers, setSelectedUsers] = useState<UsersSelectData[]>([]);
  const [eventDraft, setEventDraft] = useState<CreateEventData>({
    description: '',
    end: new Date(),
    project: '',
    start: new Date(),
    timezone: CustomTimeZones.ASIA_TBILISI,
    title: '',
    user: ''
  });
  const { data, isLoading } = useGetEvents();
  const { mutate } = useCreateEvent({});
  const classes = useStyles();

  const showModalHandler = () => {
    setShowModal(true);
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    // mapToTable
    const filteredData = data.data.filter((event: any) => {
      return event.start && event.end;
    });
    setEvents(filteredData);
  }, [data]);

  const columns = [
    {
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => {
        return <Copy text={title} />;
      },
      sorter: (a: EventData, b: EventData) => a.title.localeCompare(b.title),
      title: 'Title'
    },
    {
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => {
        return <Copy text={description} />;
      },
      sorter: (a: EventData, b: EventData) => a.description.toString().localeCompare(b.description),
      title: 'Description'
    },
    {
      dataIndex: 'timezone',
      key: 'timezone',
      render: (timezone: CustomTimeZones) => {
        return <Copy text={timezone} />;
      },
      sorter: (a: EventData, b: EventData) => a.timezone.toString().localeCompare(b.timezone),
      title: 'Timezone'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: Date) => {
        return <Copy text={formatDate(createdAt)?.toString() || ''} />;
      },
      sorter: (a: EventData, b: EventData) =>
        a.createdAt.toString().localeCompare(b.createdAt.toString()),
      title: 'Created At'
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: Date) => {
        return <Copy text={formatDate(updatedAt)?.toString() || ''} />;
      },
      sorter: (a: EventData, b: EventData) =>
        a.updatedAt.toString().localeCompare(b.updatedAt.toString()),
      title: 'Updated At'
    },
    {
      dataIndex: 'start',
      key: 'start',
      render: (start: any) => {
        return <Copy text={formatDate(start)?.toString() || ''} />;
      },
      sorter: (a: EventData, b: EventData) => a.start?.toString().localeCompare(b.start.toString()),
      title: 'Start'
    },
    {
      dataIndex: 'end',
      key: 'end',
      render: (end: any) => {
        return <Copy text={formatDate(end)?.toString() || ''} />;
      },
      sorter: (a: EventData, b: EventData) => a.end?.toString().localeCompare(b.end.toString()),
      title: 'End'
    }
  ];

  const onCancel = () => {
    setShowModal(false);
  };
  const onEventSave = () => {
    if (!eventDraft || !selectedUsers || !selectedUsers.length) return;

    selectedUsers.forEach((selectedUser) => {
      mutate({ ...eventDraft, user: selectedUser.id });
    });
  };

  const onStartDateChange = (date: Date) => {
    setStartDate(date);
    setEventDraft({ ...eventDraft, start: date });
  };
  const inEndDateChange = (date: Date) => {
    setEndDate(date);
    setEventDraft({ ...eventDraft, end: date });
  };

  const actions = [
    {
      onClick: showModalHandler,
      title: 'Create New Event',
      type: 'primary'
    }
  ];
  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={data?.paging?.total} title="Events" />
      {/* @ts-ignore */}
      <NoxTable
        actions={actions}
        checkbox={false}
        columns={columns}
        data={events}
        loading={isLoading}
        searchBy={SearchBy.TITLE}
        totalPages={data?.paging?.total}
      />
      <Modal
        footerActions={
          <>
            <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton onClick={onEventSave} type="primary" xstyle={{ height: '40px' }}>
              Add
            </CButton>
          </>
        }
        handleClose={onCancel}
        isModalOpen={showModal}
        styles={{
          // @ts-ignore
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingBottom: '24px',
            paddingTop: '12px'
          },
          width: '400px'
        }}
        titleContent={<h2 className="modal-title">New Content Notification</h2>}
      >
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-title"
          id="standard-input-push-notification-title"
          label="Title"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEventDraft({ ...eventDraft, title: e.target.value });
          }}
          size="large"
          // value={eventDraft?.title}
          width="true"
        />
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-title"
          id="standard-input-push-notification-title"
          label="description"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setEventDraft({ ...eventDraft, description: e.target.value });
          }}
          size="large"
          // value={eventDraft?.title}
          width="true"
        />
        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(e: CustomTimeZones) => {
            setEventDraft({ ...eventDraft, timezone: e });
          }}
          // defaultValue="Topic"
          suffixIcon={
            <DownOutlined
              rev={undefined}
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          // @ts-ignore
          value={CustomTimeZones?.type}
        >
          {Object.values(CustomTimeZones).map((type) => (
            <Option key={type}>{type}</Option>
          ))}
        </Select>
        <div>
          <p className={classes.dateLabel}>start</p>
          <CustomDatePicker onChange={onStartDateChange} value={startDate || ''} />
        </div>
        <div>
          <p className={classes.dateLabel}>end</p>
          <CustomDatePicker onChange={inEndDateChange} value={endDate || ''} />
        </div>
        <SelectUsersElement onChange={(value) => setSelectedUsers(value)} value={selectedUsers} />
      </Modal>
    </div>
  );
};

export default Events;
